import React, { useEffect, useMemo, useState } from "react";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// react-table components
import { useAsyncDebounce, useGlobalFilter, usePagination, useSortBy, useTable } from "react-table";

// @mui material components
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Icon from "@mui/material/Icon";

import SoftBox from "../../../components/SoftBox";
import SoftTypography from "../../../components/SoftTypography";
import SoftSelect from "../../../components/SoftSelect";
import SoftInput from "../../../components/SoftInput";
import SoftPagination from "../../../components/SoftPagination";

import DataTableHeadCell from "../../../examples/Tables/DataTable/DataTableHeadCell";
import DataTableBodyCell from "../../../examples/Tables/DataTable/DataTableBodyCell";
import TableRowsLoader from "../../../examples/Tables/DataTable/TableRowsLoader";
import TableSelectColumnMenu from "./TableSelectColumnMenu";
import TableDownloadCSV from "./TableDownloadCSV";
import { get, set } from "../../../utils/localStorage";

/** Shared table presentation; embedded tables share their surrounding panel border. */
function DataTable({
  embedded,
  entriesPerPage,
  canSearch,
  showTotalEntries,
  table,
  pagination,
  isSorted,
  noEndBorder,
  onPageSizeChange,
  onPageChange,
  pageCount,
  manualPagination,
  rowCount,
  isLoading,
  onCellClick,
  currentPage,
  columnsFilter,
  isDownloadable,
  onDownloadClick,
  filename,
  isClickable,
  onHeaderCellClick,
  sortBy,
  sortDirection,
  sortColumns,
}) {
  const defaultValue = entriesPerPage.defaultValue ? entriesPerPage.defaultValue : 10;
  const entries = entriesPerPage.entries ? entriesPerPage.entries : [5, 10, 15, 20, 25];
  const columnsCP = table.columns;
  const [columns, setColumns] = useState(table.columns); //useMemo(() => table.columns, [table]);
  // Keep the current column order and renderers while retaining saved visibility preferences.
  const renderedColumns = useMemo(
    () =>
      columnsCP.map((definition) => {
        const column = columns.find((item) => item.accessor === definition.accessor) || definition;
        const { Cell } = definition;
        return Cell ? { ...column, Cell } : column;
      }),
    [columns, columnsCP]
  );
  const data = useMemo(() => table.rows, [table]);
  const queryParams = new URLSearchParams(location.search);
  const limitParameter = queryParams.get("limit");
  const manualPaginationSettings = manualPagination ? { pageCount } : {};

  const tableInstance = useTable(
    {
      columns: renderedColumns,
      data,
      initialState: { pageIndex: 0 },
      manualPagination,
      autoResetPage: false,
      ...manualPaginationSettings,
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
    page,
    pageOptions,
    canPreviousPage,
    canNextPage,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    setGlobalFilter,
    state: { pageIndex, pageSize, globalFilter },
  } = tableInstance;

  // Set the default value for the entries per page when component mounts
  useEffect(() => {
    setPageSize(limitParameter ? limitParameter : defaultValue || 10);
    onPageSizeChange(limitParameter ? limitParameter : defaultValue || 10);
  }, [defaultValue]);

  useEffect(() => {
    if (filename && columnsFilter && get(filename)) setColumns(get(filename));
  }, []);

  // Set the entries per page value based on the select value
  const setEntriesPerPage = ({ value }) => {
    setPageSize(value);
    onPageSizeChange(value);
  };

  const handlePageChange = (option) => {
    gotoPage(option);
    onPageChange(option);
  };

  const handleGoPreviousPage = () => {
    if (currentPage) {
      gotoPage(currentPage - 1);
      onPageChange(currentPage - 2);
    } else {
      previousPage();
      onPageChange(pageIndex - 1);
    }
  };
  const handleGoNext = () => {
    if (currentPage) {
      gotoPage(currentPage + 1);
      onPageChange(currentPage);
    } else {
      nextPage();
      onPageChange(pageIndex + 1);
    }
  };

  // Render the paginations
  const renderPagination = pageOptions.map((option) => {
    return (
      <SoftPagination
        item
        key={option}
        onClick={() => handlePageChange(Number(option))}
        active={currentPage ? currentPage === option + 1 : pageIndex === option}
      >
        {option + 1}
      </SoftPagination>
    );
  });

  // Handler for the input to set the pagination index
  const handleInputPagination = ({ target: { value } }) =>
    value > pageOptions.length || value < 0 ? handlePageChange(0) : handlePageChange(Number(value));

  // Customized page options starting from 1
  const customizedPageOptions = pageOptions.map((option) => option + 1);

  // Setting value for the pagination input
  const handleInputPaginationValue = ({ target: value }) =>
    handlePageChange(Number(value.value - 1));

  // Search input value state
  const [search, setSearch] = useState(globalFilter);

  // Search input state handle
  const onSearchChange = useAsyncDebounce((value) => {
    setGlobalFilter(value || undefined);
  }, 100);

  // show/hide columns
  const handleColumnsChange = (value) => {
    set(filename, value);
    setColumns(value);
  };

  // A function that sets the sorted value for the table
  const setSortedValue = (column, isExternalSortable, accessor) => {
    if (isExternalSortable) {
      if (sortBy === accessor) {
        return sortDirection === "asc" ? "asce" : "desc";
      }

      return "none";
    }

    let sortedValue;

    if (isSorted && column.isSorted) {
      sortedValue = column.isSortedDesc ? "desc" : "asce";
    } else if (isSorted) {
      sortedValue = "none";
    } else {
      sortedValue = false;
    }

    return sortedValue;
  };

  // Setting the entries starting point
  let entriesStart = currentPage
    ? currentPage === 1
      ? currentPage
      : (currentPage - 1) * pageSize + 1
    : pageIndex === 0
      ? pageIndex + 1
      : pageIndex * pageSize + 1;
  if (data.length == 0) {
    entriesStart = 0;
  }

  // Setting the entries ending point
  let entriesEnd;
  if (currentPage) {
    if (currentPage === 1) {
      entriesEnd = pageSize > rows.length ? rows.length : pageSize;
    } else if (currentPage === pageOptions.length) {
      entriesEnd = rowCount;
    } else {
      entriesEnd = pageSize * currentPage;
    }
  } else {
    if (pageIndex === 0) {
      entriesEnd = pageSize > rows.length ? rows.length : pageSize;
    } else if (pageIndex === pageOptions.length - 1) {
      entriesEnd = manualPagination ? (rowCount ? rowCount : rows.length) : rows.length;
    } else {
      entriesEnd = pageSize * (pageIndex + 1);
    }
  }

  return (
    <SoftBox
      className="data-table"
      sx={({ palette, borders }) => ({
        minWidth: 0,
        maxWidth: "100%",
        backgroundColor: palette.white.main,
        border: embedded ? 0 : "1px solid #d9d9d1",
        borderRadius: embedded ? "0 0 22px 22px" : borders.borderRadius.xl,
      })}
    >
      {entriesPerPage || canSearch ? (
        <SoftBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          className="data-table-toolbar"
          px={{ xs: 2, sm: 2.875 }}
          py={1.75}
          gap={1.5}
          sx={({ palette }) => ({ borderBottom: "1px solid " + palette.grey[300] })}
          flexWrap="wrap"
        >
          <SoftBox display="flex" alignItems="center" gap={1}>
            {columnsFilter && (
              <TableSelectColumnMenu
                columns={columnsCP}
                onChange={handleColumnsChange}
                filename={filename}
              />
            )}
            {isDownloadable && <TableDownloadCSV onDownloadClick={onDownloadClick} />}
          </SoftBox>

          {entriesPerPage && (
            <SoftBox display="flex" alignItems="center" gap={1} ml="auto">
              <SoftBox width={76}>
                <SoftSelect
                  defaultValue={{
                    value: limitParameter ? limitParameter : defaultValue,
                    label: limitParameter ? limitParameter : defaultValue,
                  }}
                  options={entries.map((entry) => ({
                    value: entry,
                    label: entry,
                  }))}
                  onChange={setEntriesPerPage}
                  size="small"
                />
              </SoftBox>
              <SoftTypography
                variant="caption"
                color="secondary"
                sx={{ fontSize: 12.5, whiteSpace: "nowrap" }}
              >
                entries per page
              </SoftTypography>
            </SoftBox>
          )}
          {canSearch && (
            <SoftBox width="12rem" ml="auto">
              <SoftInput
                placeholder="Search..."
                value={search}
                onChange={({ currentTarget }) => {
                  setSearch(search);
                  onSearchChange(currentTarget.value);
                }}
              />
            </SoftBox>
          )}
        </SoftBox>
      ) : null}
      <TableContainer
        sx={{
          border: 0,
          borderRadius: entriesPerPage || canSearch || embedded ? 0 : "22px 22px 0 0",
          boxShadow: "none",
          "& tbody tr:last-child td": { borderBottom: 0 },
        }}
      >
        <Table {...getTableProps()}>
          <SoftBox component="thead">
            {headerGroups.map((headerGroup, key) => (
              <TableRow key={key} {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column, key) => {
                  if (!column?.hide) {
                    const accessor = column.id || column.accessor;
                    const isExternalSortable =
                      !!onHeaderCellClick &&
                      Array.isArray(sortColumns) &&
                      sortColumns.includes(accessor);
                    const headerProps = isExternalSortable
                      ? column.getHeaderProps({
                          onClick: () => onHeaderCellClick(accessor),
                        })
                      : column.getHeaderProps(isSorted && column.getSortByToggleProps());

                    return (
                      <DataTableHeadCell
                        key={key}
                        {...headerProps}
                        width={column.width ? column.width : "auto"}
                        align={column.align ? column.align : "left"}
                        sorted={setSortedValue(column, isExternalSortable, accessor)}
                      >
                        {column.render("Header")}
                      </DataTableHeadCell>
                    );
                  }
                })}
              </TableRow>
            ))}
          </SoftBox>
          <TableBody {...getTableBodyProps()}>
            {isLoading ? (
              <TableRowsLoader rowsNum={pageSize} colsNum={columns.length} />
            ) : (
              page.map((row, key) => {
                prepareRow(row);
                return (
                  <TableRow
                    key={key}
                    {...row.getRowProps()}
                    onClick={() => onCellClick(row)}
                    sx={{
                      "&:hover": {
                        backgroundColor: "#fbfcf6",
                        cursor: isClickable ? "pointer" : "default",
                      },
                    }}
                  >
                    {row.cells.map((cell, key) => {
                      if (!cell.column?.hide)
                        return (
                          <DataTableBodyCell
                            key={key}
                            noBorder={noEndBorder && rows.length - 1 === key}
                            align={cell.column.align ? cell.column.align : "left"}
                            {...cell.getCellProps()}
                          >
                            {cell.render("Cell")}
                          </DataTableBodyCell>
                        );
                    })}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <SoftBox
        display="flex"
        className="data-table-footer"
        flexWrap="wrap"
        gap={1.5}
        justifyContent="space-between"
        alignItems="center"
        px={{ xs: 2, sm: 2.875 }}
        py={!showTotalEntries && pageOptions.length === 1 ? 0 : 1.5}
        sx={({ palette }) => ({
          minHeight: !showTotalEntries && pageOptions.length === 1 ? 0 : 61,
          borderTop: "1px solid " + palette.grey[300],
        })}
      >
        {showTotalEntries && (
          <SoftBox>
            <SoftTypography
              variant="button"
              color="secondary"
              fontWeight="regular"
              sx={{ fontSize: 12.5 }}
            >
              Showing {entriesStart} to {entriesEnd} of {manualPagination ? rowCount : rows.length}{" "}
              entries
            </SoftTypography>
          </SoftBox>
        )}
        {pageOptions.length > 1 && (
          <SoftPagination
            variant={pagination.variant ? pagination.variant : "gradient"}
            color={pagination.color ? pagination.color : "info"}
          >
            {(canPreviousPage || currentPage > 1) && (
              <SoftPagination item onClick={() => handleGoPreviousPage()}>
                <Icon sx={{ fontWeight: "bold" }}>chevron_left</Icon>
              </SoftPagination>
            )}
            {renderPagination.length > 6 ? (
              <SoftBox width="5rem" mx={1}>
                <SoftInput
                  inputProps={{
                    type: "number",
                    min: 1,
                    max: customizedPageOptions.length,
                  }}
                  value={
                    currentPage
                      ? customizedPageOptions[currentPage - 1]
                      : customizedPageOptions[pageIndex]
                  }
                  onChange={(handleInputPagination, handleInputPaginationValue)}
                />
              </SoftBox>
            ) : (
              renderPagination
            )}
            {(currentPage ? renderPagination.length !== currentPage : canNextPage) && (
              <SoftPagination item onClick={() => handleGoNext()}>
                <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
              </SoftPagination>
            )}
          </SoftPagination>
        )}
      </SoftBox>
    </SoftBox>
  );
}

// Setting default values for the props of DataTable
DataTable.defaultProps = {
  embedded: false,
  entriesPerPage: { defaultValue: 10, entries: [5, 10, 15, 20, 25] },
  canSearch: false,
  showTotalEntries: true,
  pagination: { variant: "gradient", color: "info" },
  isSorted: true,
  noEndBorder: false,
  onPageSizeChange: () => void 0,
  onPageChange: () => void 0,
  pageCount: 1,
  manualPagination: false,
  rowCount: 0,
  isLoading: false,
  onCellClick: () => void 0,
  columnsFilter: false,
  isDownloadable: false,
  dataToDownload: [],
  onDownloadClick: () => void 0,
  isServerDownload: false,
  onHeaderCellClick: () => void 0,
  sortBy: "",
  sortDirection: "desc",
  sortColumns: [],
};

// Typechecking props for the DataTable
DataTable.propTypes = {
  embedded: PropTypes.bool,
  entriesPerPage: PropTypes.oneOfType([
    PropTypes.shape({
      defaultValue: PropTypes.number,
      entries: PropTypes.arrayOf(PropTypes.number),
    }),
    PropTypes.bool,
  ]),
  canSearch: PropTypes.bool,
  showTotalEntries: PropTypes.bool,
  table: PropTypes.objectOf(PropTypes.array).isRequired,
  pagination: PropTypes.shape({
    variant: PropTypes.oneOf(["contained", "gradient"]),
    color: PropTypes.oneOf([
      "primary",
      "secondary",
      "info",
      "success",
      "warning",
      "error",
      "dark",
      "light",
    ]),
  }),
  isSorted: PropTypes.bool,
  noEndBorder: PropTypes.bool,
  onPageSizeChange: PropTypes.func,
  onPageChange: PropTypes.func,
  pageCount: PropTypes.number,
  manualPagination: PropTypes.bool,
  rowCount: PropTypes.number,
  isLoading: PropTypes.bool,
  onCellClick: PropTypes.func,
  columnsFilter: PropTypes.bool,
  isDownloadable: PropTypes.bool,
  currentPage: PropTypes.number,
  onDownloadClick: PropTypes.func,
  filename: PropTypes.string,
  isClickable: PropTypes.bool,
  onHeaderCellClick: PropTypes.func,
  sortBy: PropTypes.string,
  sortDirection: PropTypes.oneOf(["asc", "desc"]),
  sortColumns: PropTypes.arrayOf(PropTypes.string),
};

export default DataTable;
